import { HttpClient } from 'api/http-client';
import {
  AddMembersRequestPayload,
  AddMembersResponse,
  ChangePlanEstimateResponse,
  ChangePlanRequestPayload,
  ChangePlanResponse,
  DownloadInvoiceResponse,
  InvoiceDetails,
  InvoiceListResponse,
  PortalSession,
  Subscription,
} from './types';

class BillingHttpClient extends HttpClient {
  public getSubscription(includeMeta: boolean): Promise<Subscription> {
    return this.get(`/subscription${includeMeta ? '?includeMeta=true' : ''}`);
  }

  public getInvoiceDetails(): Promise<InvoiceDetails> {
    return this.get('/subscription/invoice-details');
  }

  public getInvoiceList(subscriptionId: number): Promise<InvoiceListResponse> {
    return this.get(`/subscription/${subscriptionId}/invoices?limit=10`);
  }

  public createPortalSession(): Promise<PortalSession> {
    return this.post('/subscription/self-serve');
  }

  public changePlan(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanResponse> {
    return this.post('/subscription/change-plan', payload);
  }

  public changePlanEstimate(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post('/subscription/change-plan?estimate=true', payload);
  }

  public addLicences(count: number): Promise<Subscription> {
    return this.post(`/subscription/add-licences/${count}`);
  }

  public addLicencesEstimate(
    count: number,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(`/subscription/add-licences/${count}?estimate=true`);
  }

  public addMembers(
    payload: AddMembersRequestPayload,
  ): Promise<AddMembersResponse> {
    return this.post('/subscription/add-agents', payload);
  }

  public addMembersEstimate(
    payload: AddMembersRequestPayload,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post('/subscription/add-agents?estimate=true', payload);
  }

  public addPartnerMembers(
    payload: AddMembersRequestPayload,
  ): Promise<AddMembersResponse> {
    return this.post('/subscription/add-partner-agents', payload);
  }

  public addPartnerMembersEstimate(
    payload: AddMembersRequestPayload,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post('/subscription/add-partner-agents?estimate=true', payload);
  }

  public changePlanCheckout(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanResponse> {
    return this.post('/subscription/change-plan-checkout', payload);
  }

  public reactivationCheckout(
    payload: ChangePlanRequestPayload,
  ): Promise<ChangePlanResponse> {
    return this.post(
      '/subscription/change-plan-checkout?reactivate=true',
      payload,
    );
  }

  public removeScheduledPlanChange(): Promise<void> {
    return this.post('/subscription/remove-plan-changes');
  }

  public scheduleSubscriptionCancellation(formValues): Promise<void> {
    return this.post('/subscription/schedule-cancellation', formValues);
  }

  public removeScheduledCancellation(): Promise<void> {
    return this.post('/subscription/remove-cancellation');
  }

  public updateInvoiceDetails(
    payload: Partial<InvoiceDetails>,
  ): Promise<InvoiceDetails> {
    return this.post('/subscription/invoice-details', payload);
  }

  public downloadInvoice(
    subscriptionId: number,
    invoiceId: number,
  ): Promise<DownloadInvoiceResponse> {
    return this.get(
      `subscription/${subscriptionId}/invoice/${invoiceId}/download`,
    );
  }

  public forceSyncSubscription(subscriptionId: number): Promise<void> {
    return this.post(`/subscription/${subscriptionId}/force-sync`);
  }

  public reactivateMember(userId: number): Promise<AddMembersResponse> {
    return this.post(`/subscription/activate-member/${userId}`);
  }

  public reactivateMemberEstimate(
    userId: number,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(`/subscription/activate-member/${userId}?estimate=true`);
  }

  public reactivatePartnerMember(userId: number): Promise<AddMembersResponse> {
    return this.post(`/subscription/activate-partner-member/${userId}`);
  }

  public reactivatePartnerMemberEstimate(
    userId: number,
  ): Promise<ChangePlanEstimateResponse> {
    return this.post(
      `/subscription/activate-partner-member/${userId}?estimate=true`,
    );
  }
}

export const BillingAPI = new BillingHttpClient();
